import { Fragment, useMemo, useRef } from "react";
import { useIbkrData, useVisible } from "./poller";
import { ContractInfo, Quote, QuoteRow } from "./types";
import { C, Chip, IconButton, WidgetBox, companyName, expiryLabel, fmt, freshnessDot } from "./ui";
import { WidgetProps, openLadder, openLimit, referencePrice } from "./windows";

const GRID = "1.2fr 1fr 1fr 1fr 70px";

// underlying row + option rows grouped by expiry; params {underlyingConId, conIds}
export function OptionsWidget({ widget, actions }: WidgetProps) {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useVisible(ref);
  const { underlyingConId, conIds } = widget.params as { underlyingConId: number; conIds: number[] };
  const params = useMemo(() => ({ conIds: [underlyingConId, ...conIds] }), [underlyingConId, conIds]);
  const { data, freshness } = useIbkrData<QuoteRow[]>("quotes", params, visible);

  const rows = (data ?? []).filter((r): r is { contract: ContractInfo; quote: Quote } => "contract" in r);
  const underlying = rows.find((r) => r.contract.conId === underlyingConId);
  const options = rows.filter((r) => r.contract.conId !== underlyingConId);
  const groups: { expiry: string; rows: typeof options }[] = [];
  for (const r of options) {
    const expiry = r.contract.expiry ?? "";
    if (groups.length === 0 || groups[groups.length - 1].expiry !== expiry) groups.push({ expiry, rows: [] });
    groups[groups.length - 1].rows.push(r);
  }

  const row = (r: { contract: ContractInfo; quote: Quote }, name: string, bold: boolean) => (
    <div key={r.contract.conId} style={{ display: "grid", gridTemplateColumns: GRID, gap: 8, alignItems: "center", fontSize: 16, minHeight: 34 }}>
      <span style={{ fontWeight: bold ? 600 : 400 }}>{name}</span>
      <span>{fmt(r.quote.bid)}</span>
      <span style={{ fontWeight: 600 }}>{fmt(r.quote.last)}</span>
      <span>{fmt(r.quote.ask)}</span>
      <span style={{ display: "flex", gap: 6 }}>
        <IconButton icon="ladder" label={`Ladder buy / sell for ${r.contract.label}`} title="Ladder buy / sell" size={30}
          style={{ width: 32, height: 30 }} iconSize={14} onClick={() => openLadder(actions, r.contract)} />
        <IconButton icon="plus" label={`New order for ${r.contract.label}`} title="Limit order" size={30}
          style={{ width: 32, height: 30 }} iconSize={14} onClick={() => openLimit(actions, r.contract, referencePrice(r.quote))} />
      </span>
    </div>
  );

  return (
    <WidgetBox
      boxRef={ref}
      title={<span><Chip>{underlying?.contract.symbol ?? "…"}</Chip> {companyName(underlying?.contract)}</span>}
      status={freshnessDot(freshness, options[0]?.quote.dataType)}
      onDuplicate={() => actions.duplicate({ ...widget.params })}
      onClose={actions.close}
    >
      <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 2 }}>
        <div style={{ display: "grid", gridTemplateColumns: GRID, gap: 8, color: C.muted }}>
          <span></span><span>Bid:</span><span>Last:</span><span>Ask:</span><span></span>
        </div>
        {underlying && row(underlying, underlying.contract.symbol, true)}
        {groups.map((g, i) => (
          <Fragment key={g.expiry}>
            <span style={{ fontWeight: 600, margin: i === 0 ? "0 0 4px" : "8px 0 4px" }}>{expiryLabel(g.expiry)}</span>
            {g.rows.map((r) => row(r, `${fmt(r.contract.strike)} ${r.contract.right}`, false))}
          </Fragment>
        ))}
      </div>
    </WidgetBox>
  );
}
